<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="contact-management-container">
    <v-toolbar dense color="transparent" class="content-max-width elevation-0 pb-16 mt-8 mb-2">
      <v-tabs v-model="tab" background-color="transparent" class="tabs">
        <v-tab>{{ $t('tabs.contacts') }}</v-tab>
        <v-tab>{{ $t('tabs.lists') }}</v-tab>
      </v-tabs>
      <v-text-field
        v-model="searchString"
        prepend-inner-icon="mdi-magnify"
        single-line
        hide-details
        solo
        class="px-4"
        :placeholder="isContactsTab ? $t('search.contacts') : $t('search.lists')"
        @input="debouncedSearch"
      />
      <v-btn icon @click="createContact">
        <v-icon>mdi-account-plus</v-icon>
      </v-btn>
      <v-btn icon @click="createList">
        <v-icon>mdi-playlist-plus</v-icon>
      </v-btn>
    </v-toolbar>

    <section class="scrollable-section" @scroll="onScroll">
      <v-tabs-items v-model="tab" class="contact-management tab-content content-max-width">
        <v-fade-transition hide-on-leave>
          <EcsEmptySearchMessage
            v-if="!isLoading && isContactsTab && contacts.length === 0 && lastSearchString === searchString"
            :message="$t('search.empty.contacts', { searchString })"
          />
        </v-fade-transition>
        <v-fade-transition hide-on-leave>
          <EcsEmptySearchMessage
            v-if="!isLoading && isListsTab && filteredLists.length === 0"
            :message="$t('search.empty.lists', { searchString })"
          />
        </v-fade-transition>

        <v-tab-item>
          <EcsDynamicGrid v-if="isContactsTab" ref="contactsGrid" :cols="4" :gap="16">
            <EcsDynamicGridItem v-for="contact in contacts" :key="contact.id" :id="contact.id" lazy-loading>
              <template #default="{ expanded }">
                <EcsContactCard :contact="contact" :expanded="expanded" @change="search" />
              </template>
            </EcsDynamicGridItem>
          </EcsDynamicGrid>

          <v-progress-circular v-if="isLoading" width="2" color="primary" indeterminate class="loading" />
        </v-tab-item>

        <v-tab-item>
          <EcsDynamicGrid v-if="isListsTab" ref="listsGrid" :cols="4" :gap="16">
            <EcsDynamicGridItem v-for="list in filteredLists" :key="list.id" :id="list.id">
              <template #default="{ expanded }">
                <EcsContactListCard :list="list" :expanded="expanded" />
              </template>
            </EcsDynamicGridItem>
          </EcsDynamicGrid>

          <v-progress-circular v-if="isLoading" width="2" color="primary" indeterminate class="loading" />
        </v-tab-item>
      </v-tabs-items>
    </section>

    <EcsContactDialog ref="contactDialog" />
    <EcsListDialog ref="contactList" />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions, mapGetters } from 'vuex';
import EcsContactCard from '@/components/ecs-contact-card/EcsContactCard';
import EcsContactListCard from '@/components/ecs-contact-list-card/EcsContactListCard';
import EcsContactDialog from '@/components/ecs-contact-dialog/EcsContactDialog';
import EcsListDialog from '@/components/ecs-list-dialog/EcsListDialog';
import EcsDynamicGrid from '@/components/ecs-dynamic-grid/EcsDynamicGrid';
import EcsDynamicGridItem from '@/components/ecs-dynamic-grid/EcsDynamicGridItem';
import EcsEmptySearchMessage from '@/components/ecs-empty-search-message/EcsEmptySearchMessage';
import { CancelToken, isCancel } from 'axios';

export default {
  name: 'ContactManagement',

  components: {
    EcsDynamicGrid,
    EcsDynamicGridItem,
    EcsContactCard,
    EcsContactDialog,
    EcsListDialog,
    EcsContactListCard,
    EcsEmptySearchMessage
  },

  data() {
    return {
      tab: null,
      isLoading: false,
      searchString: '',
      lastSearchString: '',
      itemsPerPage: 25,
      pageNumber: 1,
      lastRequestResultsLength: 0,
      cancelSearchRequest: null
    };
  },

  computed: {
    ...mapGetters(['contacts', 'contactLists', 'filteredContactLists']),

    isContactsTab() {
      return this.tab === 0;
    },

    isListsTab() {
      return this.tab === 1;
    },

    filteredLists() {
      return this.filteredContactLists(this.searchString);
    }
  },

  created() {
    this.search();

    const itemHeight = 116;
    const itemGap = 16;
    const columns = 4;
    const itemsPerColumn = Math.ceil(document.body.clientHeight / (itemHeight + itemGap));
    this.itemsPerPage = itemsPerColumn * columns;
  },

  watch: {
    tab() {
      this.search();
      this.clearContacts();
    },
    searchString() {
      this.pageNumber = 1;
    }
  },

  methods: {
    ...mapActions(['searchContacts', 'searchLists', 'clearContacts', 'snackbarErrorMessage']),

    debouncedSearch: debounce(function() {
      this.search();
    }, 250),

    search() {
      if (this.lastSearchString !== this.searchString || this.pageNumber === 1) {
        this.clearContacts();
      }

      this.lastSearchString = this.searchString;
      if (this.isContactsTab) {
        this.handleContactsSearch();
      } else {
        this.handleListsSearch();
      }
    },

    goToContactsTab(searchString = '') {
      this.tab = 0;
      this.searchString = searchString;
    },

    goToListsTab(searchString = '') {
      this.tab = 1;
      this.searchString = searchString;
    },

    handleContactsSearch() {
      if (this.cancelSearchRequest) {
        this.cancelSearchRequest();
      }
      this.isLoading = true;

      this.searchContacts({
        query: {
          textToSearch: this.searchString,
          maxNumberOfResults: this.itemsPerPage,
          pageNumber: this.pageNumber
        },
        cancelToken: new CancelToken(cancel => {
          this.cancelSearchRequest = cancel;
        })
      })
        .then(response => {
          this.lastRequestResultsLength = response.contacts ? response.contacts.length : response.length;
          this.isLoading = false;
        })
        .catch(error => {
          if (isCancel(error)) {
            this.isLoading = true;
          } else {
            this.snackbarErrorMessage(error.message);
            this.isLoading = false;
          }
        });
    },

    loadMoreContacts() {
      this.pageNumber += 1;
      this.handleContactsSearch();
    },

    handleListsSearch() {
      if (this.contactLists.length === 0) {
        this.loadLists();
      }
    },

    loadLists() {
      this.isLoading = true;
      return this.searchLists(this.searchString).finally(() => {
        this.isLoading = false;
      });
    },

    createContact() {
      this.$root.$emit('open-contact-dialog', {
        onSave: contact => {
          this.searchString = contact.email || contact.phone;
          this.lastRequestResultsLength = 0;
          this.goToContactsTab(this.searchString);
          this.clearContacts();
          this.search();
        },
        onCancel: () => {}
      });
    },

    createList() {
      this.$refs.contactList.open().then(
        list => {
          this.goToListsTab(list.name);
          this.loadLists();
        },
        () => {}
      );
    },

    async onScroll({ target }) {
      await this.$nextTick();
      const scrollBottomReached =
        target.scrollTop + target.offsetHeight === target.scrollHeight && target.scrollTop !== 0;

      if (scrollBottomReached && this.lastRequestResultsLength !== 0 && !this.isLoading && this.contacts.length) {
        this.loadMoreContacts();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.tabs {
  flex: 0;
}
.loading {
  display: block;
  margin: 100px auto;
}
.contact-management.tab-content {
  min-height: calc(100vh - 208px);
  background: transparent;
}
.contact-management-container {
  overflow: auto;
}
.content-max-width {
  max-width: 1200px;
  margin: 0 auto;
}
.scrollable-section {
  height: calc(100vh - 168px);
  overflow: auto;
}
</style>
<style lang="scss">
.contact-management.tab-content > .v-window__container,
.contact-management.tab-content > .v-window__container > .v-window-item--active {
  min-height: calc(100vh - 240px);
}
</style>
